import { useState } from 'react';

import type { AttachmentI } from 'declarations/interfaces/attachments';
import type { NoopT } from 'declarations/types/common';
import { useDownloadAttachmentFromSignedUrl, useGetAttachmentUrl } from 'hooks/api/useAttachments';
import { useFetchTicketAttachments } from 'hooks/api/useTickets';

import { ActionTypeE } from '../declarations';

export type UseViewAttachmentsPopupT = {
  attachments: AttachmentI[] | null;
  isLoadingAttachments: boolean;
  isAttachmentsFetchingError: boolean;
  isDeleteAttachmentPopupOpened: boolean;
  handleCloseDeleteAttachmentPopup: NoopT;
  selectedAttachment: AttachmentI | null;
  isLoadingAttachmentForDownload: boolean;
  isLoadingAttachmentForPreview: boolean;
  isAttachmentForDownloadError: boolean;
  isAttachmentForPreviewError: boolean;
  handleOpenDeleteAttachmentPopup: (attachment: AttachmentI) => NoopT;
  handleAttachmentClick: (attachment: AttachmentI, actionType: ActionTypeE) => NoopT;
};

export type UseViewAttachmentsPopupParamsT = {
  ticketId: string;
};

const useViewAttachmentsPopup = ({
  ticketId,
}: UseViewAttachmentsPopupParamsT): UseViewAttachmentsPopupT => {
  const [selectedAttachment, selectAttachment] = useState<null | AttachmentI>(null);
  const [actionType, setActionType] = useState<null | ActionTypeE>(null);
  const [isDeleteAttachmentPopupOpened, setDeleteAttachmentPopupVisibility] = useState(false);

  const shouldFetchAttachments = !!ticketId;
  const shouldViewAttachment = !!selectedAttachment && actionType === ActionTypeE.View;
  const shouldDownloadAttachment = !!selectedAttachment && actionType === ActionTypeE.Download;

  const attachmentsResult = useFetchTicketAttachments({
    ticketId,
    shouldFetch: shouldFetchAttachments,
  });
  const attachmentForDownloadResult = useDownloadAttachmentFromSignedUrl({
    ticketId,
    fileName: selectedAttachment?.fileName || '',
    shouldFetch: shouldDownloadAttachment,
    onSuccess: ({ data, contentType }): void => {
      const fileName =
        (selectedAttachment?.originalName as string) || (selectedAttachment?.fileName as string);
      const blob = new Blob([data], { type: contentType });
      const href = URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = href;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);

      selectAttachment(null);
      setActionType(null);
    },
  });
  const attachmentForPreviewResult = useGetAttachmentUrl({
    ticketId,
    fileName: selectedAttachment?.fileName || '',
    shouldFetch: shouldViewAttachment,
    onSuccess: ({ value }): void => {
      window.open(value, '_blank');

      selectAttachment(null);
      setActionType(null);
    },
  });

  const handleOpenDeleteAttachmentPopup =
    (attachment: AttachmentI): (() => void) =>
    (): void => {
      setDeleteAttachmentPopupVisibility(true);
      selectAttachment(attachment);
    };
  const handleCloseDeleteAttachmentPopup = (): void => {
    setDeleteAttachmentPopupVisibility(false);
    selectAttachment(null);
  };
  const handleAttachmentClick =
    (attachment: AttachmentI, at: ActionTypeE): (() => void) =>
    (): void => {
      selectAttachment(attachment);
      setActionType(at);
    };

  return {
    attachments: attachmentsResult.data ?? null,
    isLoadingAttachments: attachmentsResult.isLoading || attachmentsResult.isRefetching,
    isAttachmentsFetchingError: attachmentsResult.isError,
    isDeleteAttachmentPopupOpened,
    handleOpenDeleteAttachmentPopup,
    handleCloseDeleteAttachmentPopup,
    selectedAttachment,
    handleAttachmentClick,
    isLoadingAttachmentForDownload: attachmentForDownloadResult.isFetching,
    isLoadingAttachmentForPreview: attachmentForPreviewResult.isFetching,
    isAttachmentForDownloadError: attachmentForDownloadResult.isError,
    isAttachmentForPreviewError: attachmentForPreviewResult.isError,
  };
};

export default useViewAttachmentsPopup;
